import React, { useRef, useState, useEffect, useMemo } from "react"
import { Form, Button, Card, Alert, Tab, Tabs, Carousel, Image, Container, Row, Col, ListGroup, Navbar, Table, Spinner } from "react-bootstrap"
import { useAuth } from "../contexts/AuthContext"
import { auth, fires} from "../firebase"
import { Link, useNavigate, Route, useParams } from "react-router-dom"
import {isMobile, isAndroid, isIOS} from 'react-device-detect';
import uuid from 'react-uuid';
import Youtube from 'react-youtube';
import { height, width } from "dom-helpers"
import firebase from 'firebase/app';
import 'firebase/firestore';
import Countdown from 'react-countdown';
import LetjoyDownloadNavBar from "./DownloadNavbar"
import { Helmet } from "react-helmet"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'


export default function EventDetails() {
    const [error, setError] = useState("")
    const [index, setIndex] = useState(0);
    const { currentUser, logout } = useAuth()
    const navigate = useNavigate()

    const [posts, setPosts] = useState([]);
    const [contents, setContents] = useState([]);
    const [agendas, setAgendas] = useState([]);
    const [orders, setOrders] = useState([]);
    const [views, setViews] = useState([]);
    const [userDetailsMap, setUserDetailsMap] = useState(new Map());
    const [userOrderCountMap, setUserOrderCountMap] = useState(new Map());
    const [eventStartDate, setEventStartDate] = useState(null);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [viewsLoading, setViewsLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    let { id } = useParams();
    const eventId = id;

    // Load orders and views for this event when id is available
    useEffect(() => {
        if (!id) return;
        const loadOrders = async () => {
            setOrdersLoading(true);
            try {
                const snapshot = await fires.collection('orders').where('eventId', '==', id).orderBy('timestamp', 'desc').get();
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), createdAt: doc.data().timestamp?.toDate?.() || new Date() }));
                setOrders(data);
            } catch (err) {
                console.error('Error loading orders:', err);
                setOrders([]);
            }
            setOrdersLoading(false);
        };
        const loadViews = async () => {
            setViewsLoading(true);
            const eventId = id;
            try {
                const snapshot = await fires.collection('events').doc(eventId).collection('views').orderBy('timestamp', 'desc').get();
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), viewedAt: doc.data().timestamp?.toDate?.() || new Date() }));
                setViews(data);
            } catch (err) {
                console.error('Error loading views:', err);
                setViews([]);
            }
            setViewsLoading(false);
        };
        loadOrders();
        loadViews();
    }, [id]);

    // Users: from orders only. Get userUID from each order (userUID, userId, uid), then fetch user doc via collection("users").doc(uid); use currentEvent === eventId as reference
    const users = useMemo(() => {
        const byUid = new Map();
        orders.forEach(o => {
            const userUID = o.userUID || o.userId || o.uid || '';
            const email = o.userEmail || o.email || '';
            if (userUID) {
                if (!byUid.has(userUID)) byUid.set(userUID, { email: email || '-', uid: userUID, source: 'order' });
            } else if (email) {
                const key = `email:${email}`;
                if (!byUid.has(key)) byUid.set(key, { email, uid: '-', source: 'order' });
            }
        });
        return Array.from(byUid.values());
    }, [orders]);

    // Load user docs: collection("users").doc(uid) for each userUID from orders; then use currentEvent === eventId for "signup because of this event"
    useEffect(() => {
        const uids = [...new Set(users.map(u => u.uid).filter(uid => uid && uid !== '-'))];
        if (uids.length === 0) {
            setUserDetailsMap(new Map());
            setUsersLoading(false);
            return;
        }
        setUsersLoading(true);
        const BATCH = 10;
        const map = new Map();
        (async () => {
            try {
                for (let i = 0; i < uids.length; i += BATCH) {
                    const batch = uids.slice(i, i + BATCH);
                    const refs = batch.map(uid => fires.collection('users').doc(uid));
                    const snaps = await Promise.all(refs.map(r => r.get()));
                    snaps.forEach((snap, j) => {
                        if (snap.exists) map.set(batch[j], { uid: batch[j], ...snap.data() });
                    });
                }
                setUserDetailsMap(new Map(map));
            } catch (err) {
                console.error('Error loading user details:', err);
                setUserDetailsMap(new Map());
            }
            setUsersLoading(false);
        })();
    }, [users]);

    // Per-user total order count (all events): for "only has this order" check
    useEffect(() => {
        const uids = [...new Set(users.map(u => u.uid).filter(uid => uid && uid !== '-'))];
        if (uids.length === 0) {
            setUserOrderCountMap(new Map());
            return;
        }
        const map = new Map();
        (async () => {
            try {
                for (const uid of uids) {
                    const snap = await fires.collection('orders').where('userUID', '==', uid).get();
                    const count = snap.empty ? (await fires.collection('orders').where('userId', '==', uid).get()).size : snap.size;
                    map.set(uid, count);
                }
                setUserOrderCountMap(new Map(map));
            } catch (err) {
                console.error('Error loading order counts per user:', err);
                setUserOrderCountMap(new Map());
            }
        })();
    }, [users]);

    // New user = created within last 90 days; old = created before that
    // Signup because of this event = 1. only has this order  2. currentEvent = eventId  3. created during or before event
    const NEW_USER_DAYS = 90;
    const userMetrics = useMemo(() => {
        const now = Date.now();
        const cutoff = now - NEW_USER_DAYS * 24 * 60 * 60 * 1000;
        const eventStartTs = eventStartDate ? (eventStartDate.getTime ? eventStartDate.getTime() : new Date(eventStartDate).getTime()) : null;
        let signupBecauseOfEvent = 0;
        let newUsers = 0;
        let oldUsers = 0;
        userDetailsMap.forEach((data, uid) => {
            const currentEvent = data.currentEvent ?? '';
            const onlyHasThisOrder = userOrderCountMap.get(uid) === 1;
            const currentEventMatches = String(currentEvent).trim() === String(eventId).trim();
            const created = data.created?.toDate?.() ?? data.createdAt?.toDate?.() ?? data.timestamp?.toDate?.() ?? null;
            const createdTs = created ? (created.getTime ? created.getTime() : new Date(created).getTime()) : null;
            const createdDuringOrBeforeEvent = eventStartTs != null && createdTs != null && createdTs <= eventStartTs;
            if (onlyHasThisOrder && currentEventMatches && createdDuringOrBeforeEvent) signupBecauseOfEvent++;
            if (createdTs != null) {
                if (createdTs >= cutoff) newUsers++;
                else oldUsers++;
            }
        });
        return { signupBecauseOfEvent, newUsers, oldUsers };
    }, [userDetailsMap, userOrderCountMap, eventId, eventStartDate]);

    // Daily aggregates for line chart: orders and views by day
    const chartDataByDay = useMemo(() => {
        const byDay = new Map();
        const toDate = (v) => (v instanceof Date ? v : (v?.toDate?.() ? v.toDate() : v ? new Date(v) : null));
        const toYMD = (d) => {
            if (!d) return null;
            const dt = d instanceof Date ? d : new Date(d);
            return dt.toISOString().slice(0, 10);
        };
        orders.forEach((o) => {
            const d = toDate(o.createdAt);
            const key = toYMD(d);
            if (key) {
                const prev = byDay.get(key) || { date: key, orders: 0, views: 0 };
                prev.orders += 1;
                byDay.set(key, prev);
            }
        });
        views.forEach((v) => {
            const d = toDate(v.viewedAt);
            const key = toYMD(d);
            if (key) {
                const prev = byDay.get(key) || { date: key, orders: 0, views: 0 };
                prev.views += 1;
                byDay.set(key, prev);
            }
        });
        return Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));
    }, [orders, views]);

    const maxW = isMobile ? "100%" : "50%"

    
    const renderer = ({ days, hours, minutes, seconds, completed }) => {
      if (completed) {
        // Render a completed state
        return <span>countdown finished</span>;
      } else {
        // Render a countdown
        return <span style={{fontSize: '25px', fontWeight: 'bold', color: 'green'}}>{days}天{hours}小时{minutes}分{seconds}秒</span>;
      }
    };

    useEffect(() => {
        return fires.collection('events').doc(id).get().then((snapshot) => {
            if (snapshot.exists) {
                const posts = [];
                const postData = snapshot.data();       
                
                if (postData.length == 0){
                  posts.push({ id: id, error: "NoAccess", error_message: "没有访问权限", error_code: 404 })
                    setPosts(posts)
                    document.title = "没有访问权限"
                    document.querySelector('meta[name="description"]').setAttribute("content","请您查看是否具有访问权限");
                    document.description = "请您查看是否具有访问权限"
                }
                else{
                    posts.push({ ...postData, id: id })
                    setPosts(posts)
                    const startDate = postData.startDate?.toDate?.() ?? postData.timestamp?.toDate?.() ?? null;
                    setEventStartDate(startDate)
                    document.title = postData.title
                    document.querySelector('meta[name="description"]').setAttribute("content",postData.description);
                    
                    if (postData.agendas != null) {
                      setAgendas(postData.agendas)
                    }


                    
                }

                
                
            }
            else{
                //deleted
                posts.push({ id: id, error: "NotFound", error_message: "没有访问权限", error_code: 404 })
                setPosts(posts)
                document.title = "没有访问权限"
                document.description = "请您查看是否具有访问权限"
            }
        });
      }, []);

      return (
        <div>
        {posts.map((post) => {

        var error = post.error;

        var postTopics = post.topics
        const viewByData = post.viewBy

        

        
        if (error == "NotFound" || error == "NoAccess") {

            return <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >

      <div className="w-100" style={{ maxWidth: maxW }}>
      <Card>
      <Helmet>
          <title>无法显示</title>
          <meta name="description" content="无法显示" />
      </Helmet>
            <Card.Body>
              <Card.Title>404</Card.Title>
              <Card.Text>该用户已删除或内容不存在</Card.Text>
              <Card.Text>User deleted or the post doesn't exist</Card.Text>
              <Card.Link href="/">返回首页</Card.Link>
            </Card.Body>
            </Card>;
          </div>
          </Container>
        }
        else{
          return <>
          <Helmet>
          <title>{post.title}</title>
          <meta name="description" content={post.description} />
          <meta name="twitter:card" content={post.photoURL} />        
          <meta name="twitter:site" content="@user" />        
          <meta name="twitter:creator" content="@user" />        
          <meta name="twitter:title" content={post.title} />        
          <meta name="twitter:description" content={post.description} />        
          <meta name="twitter:image" content={post.photoURL}/>        
          <meta property="og:title" content={post.title} />        
          <meta property="og:description" content={post.description} />        
          <meta property="og:image" content={post.photoURL} />
        <meta property="og:url" content={"https://letjoy.app/event/"+post.id} />
        <meta property="og:site_name" content={post.title} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
          </Helmet>
          <Container
          className="d-flex justify-content-center"
          style={{ minHeight: "100vh", minWidth: "100vw" }}
        >
          <div className="w-100" style={{ maxWidth: maxW, marginTop: '25px', marginBottom: '25px' }}>

          <div>
                    <Card className={isMobile ? 'border-0' : 'border-0'}>
                    
                <Card.Body>
                  
                  <Card.Text style={{marginTop: '40px', fontSize: '17px', fontWeight: 'bold', marginBottom: '5px'}}>{post.startDate.toDate().toLocaleDateString("zh")}</Card.Text>
                  <Card.Title style={{fontSize: '30px', fontWeight: 'bold'}}>{post.title}</Card.Title>
                  <Card.Text>
                  发布于 {post.timestamp.toDate().toLocaleDateString("zh")}
                  </Card.Text>

                  <Card className="mb-3 mt-3">
                    <Card.Body>
                      <Card.Title className="fs-6 mb-2">Orders & views over time (by day)</Card.Title>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                          data={chartDataByDay.map((row) => ({
                            ...row,
                            dateLabel: row.date ? `${row.date.slice(5, 7)}/${row.date.slice(8, 10)}` : ''
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Number(v).toFixed(0)} />
                          <Tooltip labelFormatter={(_, payload) => payload[0]?.payload?.date || ''} />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Line type="monotone" dataKey="orders" name="Orders" stroke="#0d6efd" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="views" name="Views" stroke="#198754" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>

                  <Tabs
                    defaultActiveKey="info"
                    id="event-tab"
                    className="mb-3"
                    style={{marginTop: '25px'}}
                  >
                    <Tab eventKey="info" title="Event info">
                    <div className="d-flex justify-content-between align-items-start" style={{marginTop: '25px', marginBottom: '10px'}}>
                    <Card.Text className="text-center" style={{ fontSize: "20px", fontWeight:'bold', color: "#000000"}}>基本信息</Card.Text>
                    </div>

                  <ListGroup style={{marginTop: '15px'}}>
                    <ListGroup.Item className="align-items-start">
                        <div>活动时间</div>
                        <div style={{marginTop: '5px', fontSize: '17px', fontWeight: 'bold'}}>{post.startDate.toDate().toLocaleString("en")}</div>
                    </ListGroup.Item>
                    <ListGroup.Item className="align-items-start">
                        <div>活动地点</div>
                        <div style={{marginTop: '5px', fontSize: '17px', fontWeight: 'bold'}}>{post.address?.fullAddress || post.address || '—'}</div>
                    </ListGroup.Item>
                    <ListGroup.Item className="align-items-start">
                        <div>参与人数</div>
                        <div style={{marginTop: '5px', fontSize: '17px', fontWeight: 'bold'}}>100-150</div>
                    </ListGroup.Item>
                </ListGroup>

                <div className="d-flex justify-content-between align-items-start" style={{marginTop: '25px', marginBottom: '10px'}}>
                    <Card.Text className="text-center" style={{ fontSize: "20px", fontWeight:'bold', color: "#000000"}}>详细介绍</Card.Text>
                    </div>
                  <Card.Text style={{marginTop: '25px', fontSize: '20px',fontWeight: 'normal'}}>
                  {post.description}
                  </Card.Text>

                {agendas && agendas.length > 0 && (
                  <>
                    <div className="d-flex justify-content-between align-items-start" style={{marginTop: '25px', marginBottom: '10px'}}>
                      <Card.Text className="text-center" style={{ fontSize: "20px", fontWeight:'bold', color: "#000000"}}>日程安排</Card.Text>
                    </div>
                    <ListGroup style={{marginTop: '15px'}}>
                      {agendas.map((agenda, i) => (
                        <ListGroup.Item key={i} className="align-items-start">
                          <div>{agenda.title}</div>
                          <div style={{marginTop: '5px', fontSize: '17px', fontWeight: 'bold'}}>A</div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                )}

                    </Tab>
                    <Tab eventKey="orders" title={`Orders (${orders.length})`}>
                    <div className="mt-3">
                      {ordersLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading orders...</div>
                      ) : (
                        <Table responsive size="sm" hover className="mb-0">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>User</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">No orders for this event</td></tr>
                            ) : (
                              orders.map((o) => (
                                <tr key={o.id}>
                                  <td className="font-monospace small">{o.id}</td>
                                  <td>{o.userEmail || o.email || o.userId || '—'}</td>
                                  <td>${(o.amount || 0).toFixed(2)}</td>
                                  <td>{o.status || '—'}</td>
                                  <td className="small">{o.createdAt instanceof Date ? o.createdAt.toLocaleString() : (o.createdAt?.toDate?.()?.toLocaleString?.() || '—')}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      )}
                    </div>
                    </Tab>
                    <Tab eventKey="users" title={`Users (${users.length})`}>
                    <div className="mt-3">
                      <Row className="mb-3 g-2">
                        <Col xs={6} md={4}>
                          <Card className="border-primary h-100">
                            <Card.Body className="py-2 px-3">
                              <Card.Text className="small text-muted mb-0">Signup because of this event</Card.Text>
                              <Card.Title className="mb-0 fs-6">{usersLoading ? '…' : userMetrics.signupBecauseOfEvent}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xs={6} md={4}>
                          <Card className="border-success h-100">
                            <Card.Body className="py-2 px-3">
                              <Card.Text className="small text-muted mb-0">New users (created in last {NEW_USER_DAYS} days)</Card.Text>
                              <Card.Title className="mb-0 fs-6">{usersLoading ? '…' : userMetrics.newUsers}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xs={6} md={4}>
                          <Card className="border-secondary h-100">
                            <Card.Body className="py-2 px-3">
                              <Card.Text className="small text-muted mb-0">Old users (created before {NEW_USER_DAYS} days)</Card.Text>
                              <Card.Title className="mb-0 fs-6">{usersLoading ? '…' : userMetrics.oldUsers}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      <Table responsive size="sm" hover className="mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Email</th>
                            <th>UID</th>
                            <th>Source</th>
                            <th>Signup from event?</th>
                            <th>Account</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-muted py-3">No users from orders for this event</td></tr>
                          ) : (
                            users.map((u, i) => {
                              const ud = u.uid && u.uid !== '-' ? userDetailsMap.get(u.uid) : null;
                              const onlyHasThisOrder = u.uid !== '-' && userOrderCountMap.get(u.uid) === 1;
                              const currentEventMatches = ud && (String(ud.currentEvent || '').trim() === String(eventId).trim());
                              const created = ud && (ud.created?.toDate?.() ?? ud.createdAt?.toDate?.() ?? ud.timestamp?.toDate?.());
                              const eventStartTs = eventStartDate ? (eventStartDate.getTime ? eventStartDate.getTime() : new Date(eventStartDate).getTime()) : null;
                              const createdDuringOrBeforeEvent = eventStartTs != null && created && (created.getTime ? created.getTime() : new Date(created).getTime()) <= eventStartTs;
                              const signupFromEvent = onlyHasThisOrder && currentEventMatches && createdDuringOrBeforeEvent;
                              const cutoff = Date.now() - NEW_USER_DAYS * 24 * 60 * 60 * 1000;
                              const isNew = created && (created.getTime ? created.getTime() : new Date(created).getTime()) >= cutoff;
                              const accountType = !ud ? '—' : created ? (isNew ? 'New' : 'Old') : '—';
                              return (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{u.email}</td>
                                  <td className="font-monospace small">{u.uid}</td>
                                  <td>{u.source}</td>
                                  <td>{usersLoading ? '…' : (signupFromEvent ? 'Yes' : 'No')}</td>
                                  <td>{usersLoading ? '…' : accountType}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                    </Tab>
                    <Tab eventKey="views" title={`Views (${views.length})`}>
                    <div className="mt-3">
                      {viewsLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading views...</div>
                      ) : (
                        <Table responsive size="sm" hover className="mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>User (UID)</th>
                              <th>Platform</th>
                              <th>Device</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {views.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">No views for this event</td></tr>
                            ) : (
                              views.map((v, i) => (
                                <tr key={v.id || i}>
                                  <td>{i + 1}</td>
                                  <td className="font-monospace small">{v.fromUID || v.uid || '—'}</td>
                                  <td>{v.platform || '—'}</td>
                                  <td>{v.device || '—'}</td>
                                  <td className="small">{v.viewedAt instanceof Date ? v.viewedAt.toLocaleString() : (v.viewedAt?.toDate?.()?.toLocaleString?.() || '—')}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      )}
                    </div>
                    </Tab>
                    </Tabs>

                </Card.Body>
                </Card>

                
                </div>
          </div>
          </Container>
          
          </>
            
        }

        
    })}
        
        
</div>
        
     )
}